//-- copyright
// GPE customization (gov.ao)
//
// Display field for actual_duration. Renders the value in days, but if the
// actual duration is greater than the planned duration appends a red `(+N)`
// slippage marker computed at render time.
//++

import { DaysDurationDisplayField } from 'core-app/shared/components/fields/display/field-types/days-duration-display-field.module';
import { InjectField } from 'core-app/shared/helpers/angular/inject-field.decorator';
import { TimezoneService } from 'core-app/core/datetime/timezone.service';
import { I18nService } from 'core-app/core/i18n/i18n.service';

export class ActualDurationDisplayField extends DaysDurationDisplayField {
  @InjectField() override timezoneService:TimezoneService;

  @InjectField() I18n:I18nService;

  public render(element:HTMLElement, displayText:string):void {
    super.render(element, displayText);

    const slippage = this.computeSlippageDays();
    if (slippage === null) {
      return;
    }

    const marker = document.createElement('span');
    if (slippage > 0) {
      // Longer than planned -> red (+N)
      marker.classList.add('op-actual-date--slippage');
      marker.textContent = ` (+${slippage})`;
      marker.title = this.I18n.t('js.work_packages.actual_dates.slippage_tooltip', {
        days: slippage,
        defaultValue: `Slippage of ${slippage} day(s) vs. planned`,
      });
    } else {
      // Same as or shorter than planned (<= 0) -> green (0) / (-N)
      marker.classList.add('op-actual-date--ahead');
      marker.textContent = ` (${slippage})`;
      marker.title = this.I18n.t('js.work_packages.actual_dates.ahead_tooltip', {
        days: Math.abs(slippage),
        defaultValue: `${Math.abs(slippage)} day(s) ahead of / on plan`,
      });
    }
    element.appendChild(marker);
  }

  private computeSlippageDays():number|null {
    const actualIso = this.value as string|null;
    const plannedIso = (this.resource as unknown as { duration?:string|null }).duration ?? null;

    const actualDays = this.parseIso8601DurationToDays(actualIso);
    const plannedDays = this.parseIso8601DurationToDays(plannedIso);

    if (actualDays === null || plannedDays === null) {
      return null;
    }

    return actualDays - plannedDays;
  }

  /**
   * The HAL representation for duration is ISO8601 (e.g. "P5D" or "PT120H").
   * We rely on the same conversion the timezone service uses internally,
   * but only need the integer day count for delta computation.
   */
  private parseIso8601DurationToDays(value:string|null):number|null {
    if (!value) {
      return null;
    }

    const days = this.timezoneService.toDays(value);
    if (typeof days !== 'number' || Number.isNaN(days)) {
      return null;
    }

    return Math.round(days);
  }
}
